/**
 * The consent and disclosure wording of the snapshot form, in one file.
 *
 * The wording of the cover intro, the sign-in step, the connect line, the tax returns step and
 * the done page. They are drafts written for founder review (crossmarc/steward-app#2158, drafted
 * 2026-10-07): change the wording here and nowhere else, and do not reword one to make a
 * screen fit. If a screen cannot show one of these truthfully, the screen is what is wrong.
 *
 * The one sentence that is RECORDED with the time it was agreed is not in this file. It is
 * `intakeSharingConsentSentence` in `@steward-app/steward-types`, so the page and the record
 * cannot drift apart; the cover renders it through that function.
 *
 * `firm` is the firm's display name and `noun` is the firm's word for the form
 * (`intakeNoun`). No string in this file may contain an em dash or an en dash
 * (`consentCopy.test.ts` checks every export).
 */

/** Cover page, the line above the recorded consent sentence. Not recorded. */
export function coverIntroLine(firm: string, noun: string): string {
  return `This is your ${noun} for ${firm}. You will be asked to create a login to protect your `
    + 'information. Your progress is saved as you go, and you can stop and come back whenever you need.';
}

/** Sign-in step, the line under the heading (the snapshot host's copy for the sign-in card). */
export const SIGN_IN_STEP_BODY =
  'Choose how you would like to sign in. A login keeps the rest of your answers protected, and it '
  + 'is how you come back later.';

/**
 * Sign-in step, the line under "we could not create a login for that address". The sign-in
 * card's own default also says to use the address an invitation went to; on the snapshot a
 * person who signs in with another address still fills in the form (which is then not attached
 * to the invitation, crossmarc/steward-app#2176), so this host shows only this sentence.
 */
export const SIGN_IN_STEP_REFUSED_BODY = 'If you already have a login, sign in.';

/**
 * Connect step, under the Connect button. This one sentence and nothing else: a founder
 * decision on the wording, relayed by the coordinator on 2026-10-07. It says nothing about
 * how long a connection lasts, what is read, or who it is shared with.
 */
export const CONNECT_DISCLOSURE = 'We never see your bank login.';

/**
 * Tax returns section: the help text under the title (crossmarc/steward-app#2431, Matt,
 * 2026-10-16). `noun` is the firm's own word for the form (`intakeNoun(firm)`), never a literal.
 * `why` is the firm's own sentence about why it wants the returns (`taxReturnsWhy`, #2187), and
 * follows as a second sentence only when the firm supplies one. The generic "do a tax analysis"
 * sentence this replaced is gone, with Matt's approval.
 */
export function taxReturnsBody(noun: string, why?: string): string {
  const first = `If desired, add one or more recent tax returns to complete your ${noun}.`;
  return why ? `${first} ${why}` : first;
}

/** Done page, the closing line once a sign-out is offered (the "close this page" phrase is dropped). */
export const DONE_CLOSING_LINE_SIGNED_OUT_OFFER =
  'To read this again later, sign in with the same login. If you think '
  + 'of something to add, let your advisor know.';

/** Done page, the closing line when no sign-out is offered. */
export const DONE_CLOSING_LINE = `You can close this page now. ${DONE_CLOSING_LINE_SIGNED_OUT_OFFER}`;
